/**
 * Therapy-Specific Data Requirements
 * 
 * Defines what patient data is required, recommended, and optional
 * for each therapy category to generate IntelliHealth recommendations.
 */

import { TherapyRequirements, TherapyCategory, DataField } from './types';

// ============================================================================
// TRT (Testosterone Replacement Therapy) Requirements
// ============================================================================

export const TRT_REQUIREMENTS: TherapyRequirements = {
  therapyCategory: 'trt',
  displayName: 'Testosterone Replacement Therapy',
  description: 'Data requirements for male hormone optimization',
  fields: [
    // Required
    {
      id: 'age',
      name: 'Age',
      description: 'Patient age for dosing and safety considerations',
      priority: 'required',
      category: 'demographics',
      checkPath: 'age'
    },
    {
      id: 'gender',
      name: 'Biological Sex',
      description: 'Required for TRT protocol selection',
      priority: 'required',
      category: 'demographics',
      checkPath: 'gender'
    },
    {
      id: 'symptoms_assessment',
      name: 'Symptom Assessment',
      description: 'Low-T symptoms via ADAM questionnaire or session data',
      priority: 'required',
      category: 'symptoms',
      checkPath: 'sessions'
    },
    // Recommended Labs
    {
      id: 'testosterone_total',
      name: 'Total Testosterone',
      description: 'Baseline testosterone level',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.testosterone'
    },
    {
      id: 'hematocrit',
      name: 'Hematocrit',
      description: 'Safety monitoring for polycythemia risk',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.hematocrit'
    },
    {
      id: 'psa',
      name: 'PSA',
      description: 'Prostate safety screening (men >40)',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.psa'
    },
    {
      id: 'estradiol',
      name: 'Estradiol',
      description: 'Estrogen level for aromatase assessment',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.estradiol'
    },
    // Optional
    {
      id: 'stop_bang',
      name: 'STOP-Bang Score',
      description: 'Sleep apnea risk assessment',
      priority: 'optional',
      category: 'questionnaire',
      checkPath: 'sessions.stop_bang'
    },
    {
      id: 'medical_history',
      name: 'Medical History',
      description: 'Cardiovascular, prostate, liver conditions',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.medical_history'
    },
    {
      id: 'lipid_panel',
      name: 'Lipid Panel',
      description: 'Cardiovascular risk assessment',
      priority: 'optional',
      category: 'labs',
      checkPath: 'lab_results.cholesterol'
    }
  ]
};

// ============================================================================
// HRT (Women's Hormone Replacement Therapy) Requirements
// ============================================================================

export const HRT_REQUIREMENTS: TherapyRequirements = {
  therapyCategory: 'hrt',
  displayName: "Women's Hormone Replacement Therapy",
  description: 'Data requirements for female hormone optimization',
  fields: [
    // Required
    {
      id: 'age',
      name: 'Age',
      description: 'Patient age for protocol selection',
      priority: 'required',
      category: 'demographics',
      checkPath: 'age'
    },
    {
      id: 'gender',
      name: 'Biological Sex',
      description: 'Required for HRT protocol',
      priority: 'required',
      category: 'demographics',
      checkPath: 'gender'
    },
    {
      id: 'menopausal_status',
      name: 'Menopausal Status',
      description: 'Pre/peri/post-menopausal status',
      priority: 'required',
      category: 'symptoms',
      checkPath: 'sessions.menopausal_status'
    },
    {
      id: 'symptoms_assessment',
      name: 'Symptom Assessment',
      description: 'Hot flashes, night sweats, mood changes, etc.',
      priority: 'required',
      category: 'symptoms',
      checkPath: 'sessions'
    },
    // Recommended
    {
      id: 'estradiol',
      name: 'Estradiol',
      description: 'Baseline estrogen level',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.estradiol'
    },
    {
      id: 'progesterone',
      name: 'Progesterone',
      description: 'Progesterone level',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.progesterone'
    },
    {
      id: 'fsh',
      name: 'FSH',
      description: 'Follicle-stimulating hormone',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.fsh'
    },
    {
      id: 'thyroid',
      name: 'Thyroid Panel',
      description: 'TSH for thyroid assessment',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.tsh'
    },
    // Optional
    {
      id: 'breast_cancer_history',
      name: 'Breast Cancer History',
      description: 'Personal/family history of breast cancer',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.breast_cancer'
    },
    {
      id: 'uterine_status',
      name: 'Uterine Status',
      description: 'Hysterectomy status for progesterone needs',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.hysterectomy'
    },
    {
      id: 'cardiovascular_history',
      name: 'Cardiovascular History',
      description: 'Heart disease, stroke, clotting disorders',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.cardiovascular'
    }
  ]
};

// ============================================================================
// GLP-1 (Semaglutide/Tirzepatide) Requirements
// ============================================================================

export const GLP1_REQUIREMENTS: TherapyRequirements = {
  therapyCategory: 'glp1',
  displayName: 'GLP-1 Receptor Agonist Therapy',
  description: 'Data requirements for metabolic optimization',
  fields: [
    // Required - Critical Safety Screening
    {
      id: 'age',
      name: 'Age',
      description: 'Patient age',
      priority: 'required',
      category: 'demographics',
      checkPath: 'age'
    },
    {
      id: 'mtc_men2_screening',
      name: 'MTC/MEN2 History',
      description: 'CRITICAL: Medullary thyroid carcinoma or MEN2 syndrome history',
      priority: 'required',
      category: 'medical_history',
      checkPath: 'sessions.mtc_men2'
    },
    {
      id: 'weight',
      name: 'Current Weight',
      description: 'Baseline weight for titration',
      priority: 'required',
      category: 'vitals',
      checkPath: 'sessions.weight'
    },
    // Recommended
    {
      id: 'bmi',
      name: 'BMI',
      description: 'Body mass index calculation',
      priority: 'recommended',
      category: 'vitals',
      checkPath: 'sessions.bmi'
    },
    {
      id: 'hba1c',
      name: 'HbA1c',
      description: 'Glycemic control assessment',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.hba1c'
    },
    {
      id: 'fasting_glucose',
      name: 'Fasting Glucose',
      description: 'Blood sugar assessment',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.glucose'
    },
    {
      id: 'lipid_panel',
      name: 'Lipid Panel',
      description: 'Cardiovascular risk assessment',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.cholesterol'
    },
    {
      id: 'kidney_function',
      name: 'Kidney Function',
      description: 'eGFR/Creatinine for renal assessment',
      priority: 'recommended',
      category: 'labs',
      checkPath: 'lab_results.egfr'
    },
    // Optional
    {
      id: 'pancreatitis_history',
      name: 'Pancreatitis History',
      description: 'History of acute pancreatitis',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.pancreatitis'
    },
    {
      id: 'gallbladder_history',
      name: 'Gallbladder Issues',
      description: 'Gallstones or cholecystitis history',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.gallbladder'
    },
    {
      id: 'diabetes_medications',
      name: 'Current Diabetes Medications',
      description: 'Insulin or sulfonylurea use',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.diabetes_meds'
    }
  ]
};

// ============================================================================
// Peptide Therapy (BPC-157, TB-500) Requirements
// ============================================================================

export const PEPTIDE_REQUIREMENTS: TherapyRequirements = {
  therapyCategory: 'peptide',
  displayName: 'Peptide Therapy (Healing/Recovery)',
  description: 'Data requirements for tissue healing peptides',
  fields: [
    // Required
    {
      id: 'age',
      name: 'Age',
      description: 'Patient age',
      priority: 'required',
      category: 'demographics',
      checkPath: 'age'
    },
    {
      id: 'primary_indication',
      name: 'Primary Indication',
      description: 'Injury type, location, or condition being treated',
      priority: 'required',
      category: 'symptoms',
      checkPath: 'sessions.indication'
    },
    // Recommended
    {
      id: 'injury_duration',
      name: 'Injury Duration',
      description: 'How long the injury/condition has persisted',
      priority: 'recommended',
      category: 'symptoms',
      checkPath: 'sessions.injury_duration'
    },
    {
      id: 'previous_treatments',
      name: 'Previous Treatments',
      description: 'Other treatments attempted',
      priority: 'recommended',
      category: 'medical_history',
      checkPath: 'sessions.previous_treatments'
    },
    {
      id: 'athlete_status',
      name: 'Competitive Athlete Status',
      description: 'WADA/USADA concerns for athletes',
      priority: 'recommended',
      category: 'medical_history',
      checkPath: 'sessions.athlete'
    },
    // Optional
    {
      id: 'liver_function',
      name: 'Liver Function',
      description: 'AST/ALT for safety monitoring',
      priority: 'optional',
      category: 'labs',
      checkPath: 'lab_results.ast'
    },
    {
      id: 'allergies',
      name: 'Allergies',
      description: 'Known drug allergies',
      priority: 'optional',
      category: 'medical_history',
      checkPath: 'sessions.allergies'
    }
  ]
};

// ============================================================================
// NAD+ Therapy Requirements
// ============================================================================

export const NAD_REQUIREMENTS: TherapyRequirements = {
  therapyCategory: 'nad',
  displayName: 'NAD+ Therapy',
  description: 'Data requirements for cellular energy optimization',
  fields: [
    // Required
    {
      id: 'age',
      name: 'Age',
      description: 'Patient age',
      priority: 'required',
      category: 'demographics',
      checkPath: 'age'
    },
    {
      id: 'goals',
      name: 'Treatment Goals',
      description: 'Energy, cognition, longevity objectives',
      priority: 'required',
      category: 'symptoms',
      checkPath: 'sessions.goals'
    },
    // Recommended
    {
      id: 'energy_assessment',
      name: 'Energy/Fatigue Assessment',
      description: 'Baseline fatigue levels',
      priority: 'recommended',
      category: 'symptoms',
      checkPath: 'sessions.energy'
    },
    {
      id: 'cognitive_assessment',
      name: 'Cognitive Function',
      description: 'Baseline cognitive concerns',
      priority: 'recommended',
      category: 'symptoms',
      checkPath: 'sessions.cognition'
    },
    // Optional
    {
      id: 'metabolic_panel',
      name: 'Metabolic Panel',
      description: 'Basic metabolic function',
      priority: 'optional',
      category: 'labs',
      checkPath: 'lab_results.metabolic'
    }
  ]
};

// ============================================================================
// General/Default Requirements
// ============================================================================

export const GENERAL_REQUIREMENTS: TherapyRequirements = {
  therapyCategory: 'general',
  displayName: 'General Wellness Assessment',
  description: 'Basic data requirements for any therapy',
  fields: [
    {
      id: 'age',
      name: 'Age',
      description: 'Patient age',
      priority: 'required',
      category: 'demographics',
      checkPath: 'age'
    },
    {
      id: 'gender',
      name: 'Biological Sex',
      description: 'Patient sex',
      priority: 'required',
      category: 'demographics',
      checkPath: 'gender'
    },
    {
      id: 'chief_complaint',
      name: 'Chief Complaint',
      description: 'Primary reason for visit',
      priority: 'required',
      category: 'symptoms',
      checkPath: 'sessions'
    },
    {
      id: 'medical_history',
      name: 'Medical History',
      description: 'Relevant medical conditions',
      priority: 'recommended',
      category: 'medical_history',
      checkPath: 'sessions.medical_history'
    },
    {
      id: 'medications',
      name: 'Current Medications',
      description: 'Active medication list',
      priority: 'recommended',
      category: 'medical_history',
      checkPath: 'sessions.medications'
    }
  ]
};

// ============================================================================
// Requirements Registry
// ============================================================================

export const THERAPY_REQUIREMENTS: Record<TherapyCategory, TherapyRequirements> = {
  trt: TRT_REQUIREMENTS,
  hrt: HRT_REQUIREMENTS,
  glp1: GLP1_REQUIREMENTS,
  peptide: PEPTIDE_REQUIREMENTS,
  nad: NAD_REQUIREMENTS,
  fertility: GENERAL_REQUIREMENTS, // Can be expanded later
  general: GENERAL_REQUIREMENTS
};

/**
 * Get requirements for a specific therapy category
 */
export function getRequirementsForTherapy(category: TherapyCategory): TherapyRequirements {
  return THERAPY_REQUIREMENTS[category] || GENERAL_REQUIREMENTS;
}

/**
 * Infer therapy category from patient data or sessions
 */
export function inferTherapyCategory(
  gender: string | number,
  sessionTypes?: string[]
): TherapyCategory {
  // Check session types for hints
  if (sessionTypes) {
    const sessionTypesLower = sessionTypes.map(s => s.toLowerCase());
    
    // TRT / Men's Health
    if (sessionTypesLower.some(s => s.includes('trt') || s.includes('testosterone') || s.includes("men's health"))) {
      return 'trt';
    }
    // HRT / Women's Health
    if (sessionTypesLower.some(s => s.includes('hrt') || s.includes('menopause') || s.includes("women's health") || (s.includes('hormone') && s.includes('women')))) {
      return 'hrt';
    }
    // GLP-1 / Weight Management
    if (sessionTypesLower.some(s => s.includes('glp') || s.includes('semaglutide') || s.includes('tirzepatide') || s.includes('weight management'))) {
      return 'glp1';
    }
    // Peptide Therapy
    if (sessionTypesLower.some(s => s.includes('bpc') || s.includes('tb-500') || s.includes('peptide') || s.includes('healing'))) {
      return 'peptide';
    }
    // NAD+
    if (sessionTypesLower.some(s => s.includes('nad'))) {
      return 'nad';
    }
    // Fertility
    if (sessionTypesLower.some(s => s.includes('fertility'))) {
      return 'fertility';
    }
  }
  
  // Gender-based default
  const genderStr = typeof gender === 'number' ? (gender === 1 ? 'male' : 'female') : gender;
  if (genderStr === 'male') {
    return 'trt';
  } else if (genderStr === 'female') {
    return 'hrt';
  }
  
  return 'general';
}
